import { useState, useCallback } from 'react';
import { Check, FlaskConical, Sparkles } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { householdCompounds } from '@/config/experiments/chem1131/experiment1.config';

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Preparing Fresh Carbonate Test Samples',
    content: `We need fresh samples because the iodine from the previous test could interfere with our observations.

**Why Fresh Samples?**
- Previous test residues may obscure bubble formation
- Clean samples ensure accurate results
- Standard analytical practice: one test per sample

**The Two Remaining Insolubles:**
1. **CaCO₃** (Calcium carbonate)
   - Found in: chalk, limestone, eggshells, antacids
   - Contains carbonate ion (CO₃²⁻)

2. **CaSO₄** (Calcium sulfate)
   - Found in: plaster of Paris, drywall, blackboard chalk
   - Contains sulfate ion (SO₄²⁻)

The key difference: Only CaCO₃ contains carbonate ions that react with acids.`
  },
  2: {
    title: 'The Carbonate-Acid Reaction',
    content: `This classic acid-base reaction produces visible CO₂ gas bubbles.

**The Chemical Equation:**
CaCO₃ + 2CH₃COOH → Ca(CH₃COO)₂ + H₂O + CO₂↑

**Breaking It Down:**
- Acetic acid (vinegar) donates H⁺ ions
- Carbonate ion accepts H⁺ to form H₂CO₃
- H₂CO₃ immediately decomposes: H₂CO₃ → H₂O + CO₂
- CO₂ gas escapes as bubbles

**Why Vinegar Works:**
- Household vinegar is ~5% acetic acid
- Strong enough to react with carbonates
- Safe to use (compared to HCl or H₂SO₄)

**Sulfates Don't React:**
CaSO₄ + CH₃COOH → No reaction (sulfate doesn't accept H⁺)`
  },
  3: {
    title: 'Observing and Recording Bubbles',
    content: `The bubble formation is the key diagnostic observation.

**What You'll See:**
| Compound | Observation | Explanation |
|----------|-------------|-------------|
| CaCO₃ | Vigorous bubbling | CO₂ gas evolution |
| CaSO₄ | No bubbles | Sulfate doesn't react |

**Bubble Characteristics:**
- Appear immediately upon acid addition
- Continuous effervescence for 30-60 seconds
- May foam if solid is finely powdered

**Common Misconception:**
Students sometimes confuse dissolved air bubbles with CO₂. True carbonate reaction bubbles:
- Form at the solid surface
- Are continuous (not just initial)
- May produce slight hissing sound

**Summary of Insolubles:**
✓ Corn starch: Iodine positive (blue-black)
✓ CaCO₃: Vinegar positive (bubbles)
✓ CaSO₄: Both tests negative`
  }
};

export default function Part4Vinegar() {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [samplesPrepared, setSamplesPrepared] = useState(false);
  const [vinegarAdded, setVinegarAdded] = useState(false);
  const [resultsRecorded, setResultsRecorded] = useState(false);
  const [testedCompounds, setTestedCompounds] = useState<Record<string, boolean>>({});
  const [currentStep, setCurrentStep] = useState(1);

  // Testing CaCO3 and CaSO4 (remaining insoluble compounds after iodine test)
  const testCompounds = [
    householdCompounds.caco3,
    householdCompounds.caso4
  ];

  const completedSteps = [samplesPrepared, vinegarAdded, resultsRecorded].filter(Boolean).length;

  const handlePrepareSamples = useCallback(() => {
    if (!samplesPrepared) {
      setSamplesPrepared(true);
      setCurrentStep(2);
    }
  }, [samplesPrepared]);

  const handleAddVinegar = useCallback((compoundId: string) => {
    if (samplesPrepared && !vinegarAdded) {
      setTestedCompounds(prev => {
        const updated = { ...prev, [compoundId]: true };
        if (Object.keys(updated).length === testCompounds.length) {
          setVinegarAdded(true);
          setCurrentStep(3);
        }
        return updated;
      });
    }
  }, [samplesPrepared, vinegarAdded, testCompounds.length]);

  const handleRecordResults = useCallback(() => {
    if (vinegarAdded && !resultsRecorded) {
      setResultsRecorded(true);
    }
  }, [vinegarAdded, resultsRecorded]);

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part IV: Vinegar Test</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Prepare Samples', hint: 'Fresh CaCO₃ and CaSO₄ in new tubes', done: samplesPrepared },
            { id: 2, title: 'Add Vinegar', hint: 'Add acetic acid to both', done: vinegarAdded },
            { id: 3, title: 'Record Results', hint: 'Note which bubbles', done: resultsRecorded }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Reaction Info */}
        <div className="reaction-panel">
          <h4>Carbonate-Acid Reaction</h4>
          <p className="equation">
            CaCO<sub>3</sub> + 2CH<sub>3</sub>COOH → Ca(CH<sub>3</sub>COO)<sub>2</sub> + H<sub>2</sub>O + CO<sub>2</sub>↑
          </p>
          <p className="explanation">
            Carbonates react with acids to produce carbon dioxide gas (bubbles).
            Sulfates do NOT react with weak acids.
          </p>
        </div>
      </div>

      <LabBench
        experimentName="Carbonate Detection"
        completedCount={completedSteps}
        totalSteps={3}
        equipment={['Vinegar (acetic acid)', 'Fresh test tubes', 'Droppers']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="vinegar-workspace">
          {/* Vinegar Bottle */}
          <div className="reagent-area">
            <div className="vinegar-bottle">
              <svg viewBox="0 0 50 90" width={60} height={100}>
                <rect x="10" y="25" width="30" height="55" rx="3" fill="#5A5A5A" />
                <rect x="13" y="30" width="24" height="45" fill="#FFF9C4" opacity="0.7" />
                <rect x="15" y="10" width="20" height="18" rx="2" fill="#333" />
                <text x="25" y="55" fontSize="5" fill="#333" textAnchor="middle" fontWeight="bold">Vinegar</text>
                <text x="25" y="65" fontSize="4" fill="#555" textAnchor="middle">Acetic Acid</text>
              </svg>
              <span className="bottle-label">White Vinegar</span>
            </div>
          </div>

          {/* Sample Preparation */}
          {!samplesPrepared && (
            <div className="prep-prompt" onClick={handlePrepareSamples}>
              <FlaskConical size={32} className="prompt-icon" />
              <h3>Prepare Fresh Samples</h3>
              <p>We need fresh samples to test for carbonates:</p>
              <div className="compound-list">
                <span className="compound-chip">CaCO<sub>3</sub> (Chalk)</span>
                <span className="compound-chip">CaSO<sub>4</sub> (Plaster)</span>
              </div>
              <button className="prep-btn">Prepare Test Tubes</button>
            </div>
          )}

          {/* Test Tubes */}
          {samplesPrepared && (
            <div className="test-section">
              <div className="tubes-container">
                {testCompounds.map((compound) => {
                  const isTested = testedCompounds[compound.id];
                  const isCarbonate = compound.carbonateReaction;

                  return (
                    <div
                      key={compound.id}
                      className={`tube-station ${currentStep === 2 && !isTested ? 'clickable' : ''}`}
                      onClick={() => {
                        if (currentStep === 2 && !isTested) {
                          handleAddVinegar(compound.id);
                        }
                      }}
                    >
                      <div className="tube-wrapper">
                        <svg viewBox="0 0 60 140" width={80} height={180}>
                          {/* Test tube */}
                          <rect x="15" y="10" width="30" height="100" rx="3" fill="rgba(200,220,240,0.3)" stroke="#7A9BB8" strokeWidth="2" />
                          <ellipse cx="30" cy="110" rx="15" ry="6" fill="rgba(200,220,240,0.4)" stroke="#7A9BB8" strokeWidth="1.5" />

                          {/* Solid compound */}
                          <ellipse cx="30" cy="100" rx="12" ry="5" fill="#F5F5F5" />

                          {/* Vinegar solution */}
                          {isTested && (
                            <rect x="17" y="50" width="26" height="50" fill="#FFF9C4" opacity="0.7" rx="2" />
                          )}

                          {/* Bubbles for carbonate */}
                          {isTested && isCarbonate && (
                            <g className="bubbles">
                              <circle cx="25" cy="85" r="3" fill="rgba(255,255,255,0.7)">
                                <animate attributeName="cy" values="85;50;30" dur="1.5s" repeatCount="indefinite" />
                                <animate attributeName="opacity" values="0.7;0.5;0" dur="1.5s" repeatCount="indefinite" />
                              </circle>
                              <circle cx="32" cy="90" r="2" fill="rgba(255,255,255,0.6)">
                                <animate attributeName="cy" values="90;55;35" dur="1.2s" repeatCount="indefinite" />
                                <animate attributeName="opacity" values="0.6;0.4;0" dur="1.2s" repeatCount="indefinite" />
                              </circle>
                              <circle cx="28" cy="95" r="2.5" fill="rgba(255,255,255,0.6)">
                                <animate attributeName="cy" values="95;60;40" dur="1.8s" repeatCount="indefinite" />
                                <animate attributeName="opacity" values="0.6;0.3;0" dur="1.8s" repeatCount="indefinite" />
                              </circle>
                              <circle cx="35" cy="88" r="1.5" fill="rgba(255,255,255,0.5)">
                                <animate attributeName="cy" values="88;48;28" dur="1s" repeatCount="indefinite" />
                                <animate attributeName="opacity" values="0.5;0.3;0" dur="1s" repeatCount="indefinite" />
                              </circle>
                              <circle cx="22" cy="92" r="2" fill="rgba(255,255,255,0.5)">
                                <animate attributeName="cy" values="92;52;32" dur="1.4s" repeatCount="indefinite" />
                                <animate attributeName="opacity" values="0.5;0.3;0" dur="1.4s" repeatCount="indefinite" />
                              </circle>
                            </g>
                          )}

                          {/* Label */}
                          <rect x="17" y="15" width="26" height="18" fill="#FFFACD" stroke="#DAA520" strokeWidth="0.5" rx="2" />
                          <text x="30" y="27" fontSize="8" fill="#333" textAnchor="middle" fontWeight="bold">
                            {compound.formula}
                          </text>
                        </svg>

                        {/* Reaction indicator */}
                        {isTested && isCarbonate && (
                          <div className="reaction-indicator">
                            <Sparkles size={16} />
                            <span>CO<sub>2</sub> evolving!</span>
                          </div>
                        )}
                      </div>

                      <span className="tube-name">{compound.commonName}</span>

                      {/* Result badge */}
                      {isTested && (
                        <span className={`result-badge ${isCarbonate ? 'positive' : 'negative'}`}>
                          {isCarbonate ? 'BUBBLES - Carbonate!' : 'No reaction'}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {currentStep === 2 && !vinegarAdded && (
                <div className="instruction-text">
                  Click each tube to add vinegar ({Object.keys(testedCompounds).length}/2)
                </div>
              )}
            </div>
          )}

          {/* Status Messages */}
          <div className="status-area">
            {vinegarAdded && !resultsRecorded && (
              <div className="status-message info" onClick={handleRecordResults} style={{ cursor: 'pointer' }}>
                <FlaskConical size={20} />
                Click to record: CaCO<sub>3</sub> produces bubbles, CaSO<sub>4</sub> does not
              </div>
            )}
            {resultsRecorded && (
              <div className="status-message success">
                <Check size={20} />
                Vinegar test complete! CaCO<sub>3</sub> (carbonate) now distinguished from CaSO<sub>4</sub> (sulfate).
              </div>
            )}
          </div>

          {/* Results Summary */}
          {resultsRecorded && (
            <div className="results-box">
              <h4>Carbonate Test Results</h4>
              <table className="results-table">
                <thead>
                  <tr>
                    <th>Compound</th>
                    <th>Observation</th>
                    <th>Conclusion</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="positive">
                    <td>CaCO<sub>3</sub></td>
                    <td>Vigorous bubbling</td>
                    <td>Carbonate confirmed</td>
                  </tr>
                  <tr>
                    <td>CaSO<sub>4</sub></td>
                    <td>No reaction</td>
                    <td>Not a carbonate (sulfate)</td>
                  </tr>
                </tbody>
              </table>
              <p className="summary">
                <strong>Insoluble compounds identified:</strong><br/>
                Corn starch (iodine +), CaCO<sub>3</sub> (vinegar +), CaSO<sub>4</sub> (both tests -)
              </p>
            </div>
          )}
        </div>

        <style>{`
          .vinegar-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .reagent-area {
            display: flex;
            justify-content: center;
          }

          .vinegar-bottle {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .bottle-label {
            font-size: 12px;
            color: var(--text-secondary);
          }

          .prep-prompt {
            background: var(--bg-elevated);
            border: 2px solid var(--border);
            border-radius: 12px;
            padding: 24px;
            text-align: center;
            max-width: 400px;
            cursor: pointer;
            transition: all 0.2s;
          }

          .prep-prompt:hover {
            border-color: var(--secondary);
          }

          .prompt-icon {
            color: var(--secondary);
            margin-bottom: 12px;
          }

          .prep-prompt h3 {
            margin: 0 0 8px 0;
            color: var(--text-primary);
          }

          .prep-prompt p {
            margin: 0 0 16px 0;
            color: var(--text-secondary);
            font-size: 14px;
          }

          .compound-list {
            display: flex;
            gap: 12px;
            justify-content: center;
            margin-bottom: 16px;
          }

          .compound-chip {
            background: var(--bg-surface);
            padding: 8px 16px;
            border-radius: 20px;
            font-size: 14px;
            color: var(--text-primary);
          }

          .prep-btn {
            background: var(--secondary);
            color: white;
            border: none;
            padding: 10px 24px;
            border-radius: 6px;
            font-weight: 600;
            cursor: pointer;
          }

          .test-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 16px;
          }

          .tubes-container {
            display: flex;
            gap: 50px;
            background: linear-gradient(180deg, #5C534A 0%, #3D352F 100%);
            padding: 25px 40px;
            border-radius: 10px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          }

          .tube-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s;
          }

          .tube-station.clickable {
            cursor: pointer;
          }

          .tube-station.clickable:hover {
            background: rgba(255,255,255,0.1);
          }

          .tube-wrapper {
            position: relative;
          }

          .reaction-indicator {
            position: absolute;
            top: 10px;
            right: -30px;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2px;
            color: #60A5FA;
            font-size: 10px;
            animation: pulse 1s ease-in-out infinite;
          }

          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }

          .tube-name {
            font-size: 12px;
            color: #CCC;
          }

          .result-badge {
            font-size: 11px;
            padding: 4px 10px;
            border-radius: 4px;
            font-weight: 600;
            text-align: center;
          }

          .result-badge.positive {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .result-badge.negative {
            background: rgba(150, 150, 150, 0.2);
            color: #999;
          }

          .instruction-text {
            color: #60A5FA;
            font-size: 14px;
          }

          .reaction-panel {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 12px;
            margin-top: 16px;
          }

          .reaction-panel h4 {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--secondary);
          }

          .reaction-panel .equation {
            background: var(--bg-surface);
            padding: 8px;
            border-radius: 4px;
            font-family: monospace;
            font-size: 11px;
            margin: 0 0 8px 0;
            color: var(--text-primary);
          }

          .reaction-panel .explanation {
            margin: 0;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .results-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 450px;
            border-left: 3px solid var(--secondary);
          }

          .results-box h4 {
            margin: 0 0 12px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .results-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 12px;
            font-size: 13px;
          }

          .results-table th, .results-table td {
            padding: 8px;
            text-align: left;
            border-bottom: 1px solid var(--border);
          }

          .results-table th {
            color: var(--text-secondary);
            font-weight: 600;
          }

          .results-table tr.positive {
            background: rgba(0, 168, 107, 0.1);
          }

          .results-table tr.positive td {
            color: var(--secondary);
          }

          .summary {
            margin: 0;
            font-size: 12px;
            color: var(--text-secondary);
            line-height: 1.6;
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp1"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
